import React from 'react';
import styled from 'styled-components';
import { Card as AntCard, Divider as AntDivider, Select as AntSelect, Space } from 'antd';
import { t, jt } from 'ttag';

import BGTopLeft from '../../assets/login-bg-top-left.png';
import BGBottomRight from '../../assets/login-bg-bottom-right.png';
import LogoImage from '../../assets/EM-logo-colour-dark.svg';
import useTranslation from '../../hooks/useTranslation';
import { ISO_LOCALE_MAP, LANGUAGE_BY_ISO } from '../../services/translation/api';
import { EVENTMOBI_URLS } from '../../constants/ExternalUrls';

const Background = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  background-image: url(${BGTopLeft}), url(${BGBottomRight});
  background-position:
    top left,
    bottom right;
  background-size: auto 75%;
  background-repeat: no-repeat;
  width: 100%;
`;

const Logo = styled.img`
  display: block;
  width: 250px;
  margin-bottom: 20px;
`;

const Card = styled(AntCard)`
  border-radius: 10px;
  @media (max-width: 600px) {
    border-radius: unset;
  }
  .ant-card-body {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 500px;
    @media (max-width: 600px) {
      width: 100vw;
      height: 100vh;
      min-height: 600px;
    }
  }
`;

const Select = styled(AntSelect)`
  position: absolute;
  top: -32px;
  right: 0;
  width: fit-content;
  color: white;
  .ant-select-selection-item {
    height: 26px;
    text-align: right;
  }
  .ant-select-selection-search {
    margin-left: 0;
  }
  .ant-select-arrow {
    color: inherit;
  }

  @media (max-width: 600px) {
    top: 0;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-size: 12px;
  margin-top: 24px;
  color: #595959;

  @media (max-width: 600px) {
    padding: 15px;
    flex: 1;
    align-items: flex-end;
  }
`;

const Content = styled.div`
  width: 100%;

  @media (max-width: 600px) {
    margin-top: 50px;
  }
`;

const Divider = styled(AntDivider)`
  border-left: 1px solid #686868;
`;

type Props = {
  children: React.ReactNode;
};

const Layout = ({ children }: Props) => {
  const { language, setLanguage } = useTranslation();

  const EMLink = (
    <a
      key="em"
      href={EVENTMOBI_URLS.HOME_WITH_UTM}
      rel="nofollow noopener noreferrer"
      target="_blank"
    >
      EventMobi
    </a>
  );

  return (
    <Background>
      <Card>
        <Select
          variant="borderless"
          placeholder={t`Language`}
          value={language}
          onChange={(lang: string) => setLanguage(lang)}
          popupMatchSelectWidth={false}
          style={{ color: '#595959' }}
          options={Object.keys(ISO_LOCALE_MAP).map(code => ({
            value: code,
            label: LANGUAGE_BY_ISO(code),
          }))}
        />
        <Logo src={LogoImage} alt="EventMobi" />
        <Content>{children}</Content>
        <Footer>
          <Space separator={<Divider orientation="vertical" />}>
            <a
              href={EVENTMOBI_URLS.TERMS_OF_USE}
              rel="nofollow noopener noreferrer"
              target="_blank"
            >{t`Terms of Use`}</a>
            <a
              href={EVENTMOBI_URLS.PRIVACY_POLICY}
              rel="nofollow noopener noreferrer"
              target="_blank"
            >{t`Privacy Policy`}</a>
          </Space>
          <div>{jt`Powered by ${EMLink}™`}</div>
        </Footer>
      </Card>
    </Background>
  );
};

export default Layout;
